/**
 * CertificatesHub — iter-217c
 *
 * 4 category buttons:
 *   - merchants  → منصة التجار
 *   - customers  → منصة العملاء
 *   - company    → للشركة (recipient = another company)
 *   - employees  → للموظفين (registered ASB606 admin/staff)
 *
 * Each opens the CertificateCreatePage for that bucket.
 */
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Store, Users, Building2, Briefcase, ChevronLeft, ChevronRight } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';

export default function CertificatesHub() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const Arrow = isAr ? ChevronLeft : ChevronRight;

  const CATEGORIES = [
    { id: 'merchant', titleAr: 'منصة التجار',  titleEn: 'Merchants',  icon: Store,      color: '#8B5CF6' },
    { id: 'customer', titleAr: 'منصة العملاء', titleEn: 'Customers',  icon: Users,      color: '#1E9BD9' },
    { id: 'company',  titleAr: 'للشركة',        titleEn: 'Company',    icon: Building2,  color: '#F59E0B' },
    { id: 'employee', titleAr: 'للموظفين',     titleEn: 'Employees',  icon: Briefcase,   color: '#10b981' },
  ];

  return (
    <DashboardLayout
      title="شهادات لمن يهمه الأمر والتهنئة"
      titleEn="Certificates & Congratulations"
      subtitle="اختر فئة المستلم لإصدار شهادة على ورقة CloudGM"
      subtitleEn="Choose a recipient category to issue a formal certificate on CloudGM letterhead"
      screenCode="FZLC-CERT"
      backPath="/manage/cloudgm-fz-llc"
      onBack={() => navigate('/manage/cloudgm-fz-llc')}
      breadcrumb={[
        { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
        { labelAr: 'إدارة شركة CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC Management', path: '/manage/cloudgm-fz-llc' },
        { labelAr: 'شهادات لمن يهمه الأمر والتهنئة', labelEn: 'Certificates & Congratulations' },
      ]}
    >
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="certificates-hub">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CATEGORIES.map((c, i) => (
            <motion.button
              key={c.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              whileHover={{ y: -6, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(`/manage/cloudgm-fz-llc/certificates/${c.id}`)}
              className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border transition-all text-start"
              style={{ borderColor: `${c.color}30` }}
              data-testid={`cert-cat-${c.id}`}
            >
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center border transition-transform group-hover:scale-110 mb-3"
                style={{ backgroundColor: `${c.color}15`, borderColor: `${c.color}40` }}
              >
                <c.icon className="h-7 w-7" style={{ color: c.color }} />
              </div>
              <h3 className="text-base font-bold text-white mb-1 flex items-center gap-2">
                {isAr ? c.titleAr : c.titleEn}
                <Arrow className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: c.color }} />
              </h3>
              <p className="text-xs text-white/50">
                {t('إصدار شهادة جديدة', 'Issue a new certificate')}
              </p>
            </motion.button>
          ))}
        </div>

        {/* Recently issued */}
        <div className="mt-8 text-center text-xs text-white/40">
          {t('التحكم بمواضيع الشهادات وأسم الشركة يتم من: التحكم والمعلومات ▸ CLOUDGM FZ LLC (للتحكم) ▸ التحكم بإدارة الشركة',
             'Manage subjects & company name from: Platform Control ▸ CLOUDGM FZ LLC (Control) ▸ Company Management Control')}
        </div>
      </section>
    </DashboardLayout>
  );
}
